// Simple View job-stage pane. One stage pipeline for the whole job
// instead of per-spool pipelines. Mirrors the SpoolCard pipeline +
// reuses SpoolStageDetailsTable for the editable stage grid, but the
// stages are a modal-local draft (persisted on Save via
// replaceJobStages) rather than per-stage PATCH.
import { useMemo, useState } from "react";
import type {
  SpoolStageDto,
  StageDefDto,
  StageKey,
  StageStatus,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "./StagesProvider";
import { addDays, fmtDate, isoToday } from "../lib/dates";
import {
  currentStage,
  denseStages,
  inferWeldBayKey,
  stageDays,
} from "../lib/stageHelpers";
import { SpoolStageDetailsTable } from "./SpoolStageDetailsTable";

interface Props {
  jobStages: SpoolStageDto[];
  onChange(next: SpoolStageDto[]): void;
  team: TeamMemberDto[];
  jobBay: number | null;
  // Anchor for Auto-schedule + the suggested due date it produces.
  jobStartDate: string | null;
  onSuggestDueDate(date: string): void;
}

export function JobStageCard({
  jobStages,
  onChange,
  team,
  jobBay,
  jobStartDate,
  onSuggestDueDate,
}: Props) {
  const catalogue = useActiveStages();
  const [showAll, setShowAll] = useState(false);
  const stageByKey = useMemo(() => {
    const m = new Map<string, StageDefDto>();
    for (const s of catalogue) m.set(s.stageKey, s);
    return m;
  }, [catalogue]);

  const weldBayKey = useMemo(
    () => inferWeldBayKey(jobStages, jobBay),
    [jobStages, jobBay],
  );
  const dense = useMemo(
    () => denseStages(jobStages, weldBayKey, undefined, catalogue).map((s) => ({ ...s, spoolId: 0 })),
    [jobStages, weldBayKey, catalogue],
  );
  // A job pipeline can weld in several bays at once (unlike a spool,
  // which runs exactly one), so the active list is simply every
  // non-skipped row — no chosen-bay collapse. The pipeline strip and
  // the progress math both walk this list, so each active bay is its
  // own pill + its own progress tick.
  const active = useMemo(() => dense.filter((s) => s.status !== "skipped"), [dense]);
  const cur = currentStage(active);
  const curDef = cur ? stageByKey.get(cur.stageKey) : null;
  const completeCount = active.filter((s) => s.status === "complete").length;
  const pct = active.length > 0 ? Math.round((completeCount / active.length) * 100) : 0;
  const allComplete = active.length > 0 && completeCount === active.length;
  const pipelineRows = active;
  const today = isoToday();

  // Merge a patch into the draft, lazy-seeding the row if it's a
  // synthetic (catalogue-only) stage. Same contract the
  // SpoolStageDetailsTable onStagePatch expects.
  const applyPatch = (
    stageKey: StageKey,
    patch: {
      hours?: number;
      assigneeId?: number | null;
      startDate?: string | null;
      doneDate?: string | null;
      status?: StageStatus;
    },
  ) => {
    const has = jobStages.some((s) => s.stageKey === stageKey);
    if (has) {
      onChange(jobStages.map((s) => (s.stageKey === stageKey ? { ...s, ...patch } : s)));
      return;
    }
    onChange([
      ...jobStages,
      {
        id: -1,
        spoolId: 0,
        stageKey,
        status: patch.status ?? "pending",
        hours: patch.hours ?? 0,
        assigneeId: patch.assigneeId ?? null,
        startDate: patch.startDate ?? null,
        doneDate: patch.doneDate ?? null,
      },
    ]);
  };

  // Advance to a target stage: every earlier active stage flips to
  // complete (seeding a start so the table doesn't show a finish
  // without a start), and the target goes in-progress. Mirrors
  // SpoolCard.advanceTo.
  const advanceTo = (target: SpoolStageDto) => {
    if (target.status === "in-progress" || target.status === "complete" || target.status === "skipped") return;
    const idx = active.findIndex((s) => s.stageKey === target.stageKey);
    if (idx < 0) return;
    let next = jobStages.slice();
    const merge = (key: string, patch: Partial<SpoolStageDto>) => {
      const has = next.some((s) => s.stageKey === key);
      next = has
        ? next.map((s) => (s.stageKey === key ? { ...s, ...patch } : s))
        : [...next, { id: -1, spoolId: 0, stageKey: key as StageKey, status: "pending", hours: 0, assigneeId: null, startDate: null, doneDate: null, ...patch }];
    };
    for (let i = 0; i < idx; i++) {
      const s = active[i]!;
      if (s.status === "complete") continue;
      merge(s.stageKey, { status: "complete", startDate: s.startDate ?? today, doneDate: s.doneDate ?? today });
    }
    merge(target.stageKey, { status: "in-progress", startDate: target.startDate ?? today });
    onChange(next);
  };

  const onProgress = () => {
    if (allComplete || active.length === 0) return;
    const inProg = active.find((s) => s.status === "in-progress");
    if (inProg) {
      const i = active.indexOf(inProg);
      const nextStage = active.slice(i + 1).find((s) => s.status !== "complete");
      if (nextStage) {
        advanceTo(nextStage);
      } else {
        applyPatch(inProg.stageKey as StageKey, { status: "complete", doneDate: inProg.doneDate ?? today });
      }
      return;
    }
    const firstPending = active.find((s) => s.status === "pending");
    if (firstPending) advanceTo(firstPending);
  };

  // Cascade FS dates across active stages from the job start, then
  // suggest the resulting finish as the due date.
  const autoSchedule = () => {
    let cursor = jobStartDate || today;
    let next = jobStages.slice();
    for (const def of catalogue) {
      const row = active.find((s) => s.stageKey === def.stageKey);
      if (!row) continue;
      const start = cursor;
      next = next.map((s) => (s.stageKey === def.stageKey ? { ...s, startDate: start } : s));
      cursor = addDays(start, stageDays(row, catalogue));
    }
    onChange(next);
    onSuggestDueDate(cursor);
  };

  const onPillClick = (st: SpoolStageDto) => advanceTo(st);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "var(--bsp-font-display)", fontSize: 11, fontWeight: 700, color: "var(--bsp-blue)", textTransform: "uppercase", letterSpacing: "0.14em" }}>
          Job Stages
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" className="btn-sm" onClick={autoSchedule} title="Cascade dates: each stage starts when the previous finishes; suggests the due date">
            ⟳ Auto-schedule
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={onProgress}
            disabled={allComplete || active.length === 0}
            title="Advance the current stage: pending → in-progress → complete → next stage"
          >
            {allComplete ? "✓ Done" : "Progress ▶"}
          </button>
        </div>
      </div>

      <div style={{
        background: "var(--bsp-blue-pale, rgba(27,68,156,0.06))",
        border: "1px solid var(--bsp-blue-border, rgba(27,68,156,0.30))",
        borderRadius: "var(--bsp-r-md, 6px)",
        padding: "8px 12px",
        fontSize: 11,
        lineHeight: 1.45,
        color: "var(--bsp-dark)",
        marginBottom: 10,
      }}>
        <strong>Simple view</strong> — one stage pipeline for the whole job. Spool stage
        tracking is off; Welding Bays, Cutting and Resource views use the job start and due
        dates. Switch to Detailed in the header to track stages per spool.
      </div>

      <div className="sp-card" style={{ border: "1px solid var(--bsp-border)", borderRadius: "var(--bsp-r-md, 6px)", boxShadow: "var(--bsp-shadow-card)", padding: 12 }}>
        <div className="pipeline-strip">
          {pipelineRows.length === 0 ? (
            <div style={{ flex: 1, fontSize: 10, color: "var(--bsp-fg-muted)", fontStyle: "italic", display: "flex", alignItems: "center", justifyContent: "center", padding: 6 }}>
              No active stages — toggle the eye in the stage table to show all.
            </div>
          ) : (
            pipelineRows.map((st) => {
              const def = stageByKey.get(st.stageKey);
              if (!def) return null;
              const isCur = cur?.stageKey === st.stageKey && st.status !== "complete";
              const cls = ["pipeline-cell", `is-${st.status}`, isCur ? "is-current" : ""].filter(Boolean).join(" ");
              const tip = [
                def.name,
                st.hours ? `${st.hours}h` : null,
                st.startDate ? `· ${fmtDate(st.startDate)}` : null,
                `→ ${st.status}`,
              ].filter(Boolean).join(" · ");
              return (
                <button key={st.stageKey} type="button" className={cls} style={{ background: def.color }} title={tip} onClick={() => onPillClick(st)}>
                  {def.short}
                </button>
              );
            })
          )}
        </div>
        <div className="pipeline-foot">
          <div className="pipeline-foot-left">
            {cur && !allComplete ? (
              <>
                <span className="dot" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: stageByKey.get(cur.stageKey)?.color ?? "#9CA09F" }} />
                <span>{curDef?.name ?? cur.stageKey}{cur.startDate ? ` · started ${fmtDate(cur.startDate)}` : ""}</span>
              </>
            ) : allComplete ? (
              <span>✓ All active stages complete</span>
            ) : (
              <span style={{ color: "var(--bsp-fg-muted)", fontWeight: 500 }}>No active stages</span>
            )}
          </div>
          <div className="pipeline-progress">
            <div className="pipeline-progress-bar" style={{ width: `${pct}%` }} />
          </div>
          <span style={{ minWidth: 34, textAlign: "right", fontFamily: "var(--bsp-font-mono)", fontWeight: 700 }}>{pct}%</span>
        </div>
        <SpoolStageDetailsTable
          stages={dense}
          team={team}
          weldBayKey={weldBayKey}
          showAll={showAll}
          onToggleShowAll={() => setShowAll((v) => !v)}
          multiBay
          onStagePatch={(k, p) => applyPatch(k, p)}
        />
      </div>
    </div>
  );
}
